// THIS COMPONENT IS USED WITH BOOKINGS AND BOOKING SESSIONS AS WELL
// mui
import {
  Grid,
  IconButton,
  InputAdornment,
  Skeleton,
  TextField,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// components
import Iconify from '@/components/Iconify';
import DropdownSelection from '@/components/DropdownSelection';
//
import useResponsive from '@/hooks/useResponsive';
//types
import { Settings } from '@/types';
import { format, isValid, parseISO } from 'date-fns';

type BookingsTableToolbarProps = {
  filterService: number;
  filterServiceType: number;
  filterSearchBar: string;
  filterStartDate: string | null;
  filterEndDate: string | null;
  filterStatusId?: number;
  filterPaymentStatusId?: number;
  //
  handleFilterDropdown: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSearchField: (value: string) => void;
  handleDatepickerChange: (
    value: string | null,
    date_type: string,
    keyboardInputValue?: string | undefined
  ) => void;
  resetFilters: () => void;
  //
  settingsData: Settings | undefined;
  showStatusFilters?: boolean;
};

export default function BookingsTableToolbar({
  filterService,
  filterServiceType,
  filterSearchBar,
  filterStartDate,
  filterEndDate,
  filterStatusId = 0,
  filterPaymentStatusId = 0,
  //
  handleFilterDropdown,
  handleSearchField,
  handleDatepickerChange,
  resetFilters,
  //
  settingsData,
  showStatusFilters = false,
}: BookingsTableToolbarProps) {
  const downXl = useResponsive('down', 'xl');
  if (!settingsData) return <Skeleton variant="rectangular" height={64} />;

  const parseDateValue = (value: string | null) => {
    if (!value) return null;
    const parsed = parseISO(value);
    return isValid(parsed) ? parsed : null;
  };

  const formatDateForParam = (date: Date | null) =>
    date && isValid(date) ? format(date, 'yyyy-MM-dd') : null;

  const startDateValue = parseDateValue(filterStartDate);
  const endDateValue = parseDateValue(filterEndDate);

  const isFiltering =
    filterService !== 0 ||
    filterServiceType !== 0 ||
    !!filterStartDate ||
    !!filterEndDate ||
    !!filterSearchBar ||
    (showStatusFilters && filterStatusId !== 0) ||
    (showStatusFilters && filterPaymentStatusId !== 0);

  const BASE_ARRAY = [{ id: 0, name: 'All' }];

  const serviceVariants = [
    ...BASE_ARRAY,
    ...settingsData.serviceVariants
      .filter((variant) => variant.service_id === filterService)
      .map((item) => ({
        id: item.id,
        name: item.name,
      })),
  ];

  const services = [...BASE_ARRAY, ...settingsData.services];

  const statusOptions = [
    ...BASE_ARRAY,
    ...settingsData.bookingSessionStatuses.map((item) => ({
      id: item.id,
      name: item.display,
    })),
  ];

  const paymentStatusOptions = [
    ...BASE_ARRAY,
    ...settingsData.bookingPaymentStatuses.map((item) => ({
      id: item.id,
      name: item.display,
    })),
  ];

  const xlDropdownGridItemSize = showStatusFilters ? 1.5 : 2;
  const xlSearchbarSize = showStatusFilters ? 3 : 4;

  return (
    <Grid
      container
      spacing={2}
      direction={{ xs: 'column', lg: 'row' }}
      sx={{ py: 2.5, px: 3 }}
    >
      {/* SERVICE STATUS */}
      {showStatusFilters && (
        <>
          <Grid item xs={12} md={6} xl={xlDropdownGridItemSize}>
            <DropdownSelection
              name="status"
              label="Session status"
              filterDropdownValue={filterStatusId}
              listOptions={statusOptions}
              handleFilterDropdown={handleFilterDropdown}
            />
          </Grid>

          <Grid item xs={12} md={6} xl={xlDropdownGridItemSize}>
            <DropdownSelection
              name="payment_status"
              label="Payment status"
              filterDropdownValue={filterPaymentStatusId}
              listOptions={paymentStatusOptions}
              handleFilterDropdown={handleFilterDropdown}
            />
          </Grid>
        </>
      )}
      {/* SERVICE NAME */}
      <Grid item xs={12} md={6} xl={xlDropdownGridItemSize}>
        <DropdownSelection
          name="service"
          label="Service"
          filterDropdownValue={filterService}
          listOptions={services}
          handleFilterDropdown={handleFilterDropdown}
        />
      </Grid>

      {/* SERVICE TYPE */}
      <Grid item xs={12} md={6} xl={xlDropdownGridItemSize}>
        <DropdownSelection
          name="service_type"
          label="Service Type"
          disabled={filterService === 0}
          filterDropdownValue={filterServiceType}
          listOptions={serviceVariants}
          handleFilterDropdown={handleFilterDropdown}
        />
      </Grid>

      {/* START DATE */}
      <Grid item xs={12} md={6} xl={xlDropdownGridItemSize}>
        <DatePicker
          label="Start Date"
          value={startDateValue}
          onChange={(date) =>
            handleDatepickerChange(formatDateForParam(date), 'start')
          }
          renderInput={(params) => (
            <TextField {...params} size="small" fullWidth />
          )}
          componentsProps={{
            actionBar: {
              actions: ['clear'],
            },
          }}
        />
      </Grid>

      {/* END DATE */}
      <Grid item xs={12} md={6} xl={xlDropdownGridItemSize}>
        <DatePicker
          label="End Date"
          value={endDateValue}
          minDate={startDateValue}
          onChange={(date) =>
            handleDatepickerChange(formatDateForParam(date), 'end')
          }
          renderInput={(params) => (
            <TextField {...params} size="small" fullWidth />
          )}
          componentsProps={{
            actionBar: {
              actions: ['clear'],
            },
          }}
        />
      </Grid>

      <Grid
        item
        container
        direction={{ xs: 'row', md: 'row', xl: 'row' }}
        spacing={2}
        xs={12}
        md={12}
        xl={xlSearchbarSize}
        alignItems="center"
      >
        <Grid item xs={12} md={12} xl={isFiltering ? 10.5 : 12}>
          <TextField
            fullWidth
            value={filterSearchBar}
            onChange={(event) => handleSearchField(event.target.value)}
            placeholder="Search in bookings..."
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify
                    icon={'eva:search-fill'}
                    sx={{ color: 'text.disabled', width: 20, height: 20 }}
                  />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        {isFiltering && (
          <Grid item xs={12} md={12} xl={1} justifyContent="center">
            <Tooltip title="Reset filters" placement="top">
              <span>
                <IconButton
                  color="inherit"
                  onClick={resetFilters}
                  sx={{
                    fontSize: 14,
                    display: 'flex',
                    direction: 'row',
                    gap: 0.5,
                  }}
                >
                  <Iconify
                    icon="eva:close-circle-outline"
                    sx={{ width: 20, height: 20 }}
                  />
                  {downXl && 'Reset filters'}
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}
